import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, AlertCircle, CheckCircle2, MessageSquare, Ticket, Building2, BarChart3 } from 'lucide-react';
import { api } from '@/api/client';

interface HealthStatus {
  app: string;
  database: string;
  kb: string;
  php_version?: string;
  timestamp?: string;
}

interface DashboardStats {
  requests_today: number;
  requests_this_month: number;
  tickets: { total: number; open: number; pending: number; closed: number; resolved: number };
  tenants_total: number;
  tenants_active: number;
}

export function Dashboard() {
  const [health, setHealth]   = useState<HealthStatus | null>(null);
  const [stats, setStats]     = useState<DashboardStats | null>(null);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    api.health()
      .then(setHealth)
      .catch((e) => setError(e.message));

    api.dashboardStats()
      .then(setStats)
      .catch(() => { /* non-critical — stats failure shouldn't block the dashboard */ });
  }, []);

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your support system</p>
        </div>
        <Link to="/workflows/new" className="btn-primary">
          <Plus size={16} className="mr-2" />
          New Workflow
        </Link>
      </div>

      {/* ── Activity Stats ─────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<BarChart3 size={18} className="text-brand-600 dark:text-brand-400" />}
            label="Requests Today"
            value={stats.requests_today.toLocaleString()}
            sub={`${stats.requests_this_month.toLocaleString()} this month`}
            color="blue"
          />
          <StatCard
            icon={<Ticket size={18} className="text-amber-600 dark:text-amber-400" />}
            label="Open Tickets"
            value={stats.tickets.open.toString()}
            sub={`${stats.tickets.pending} pending · ${stats.tickets.total} total`}
            color="amber"
          />
          <StatCard
            icon={<CheckCircle2 size={18} className="text-green-600 dark:text-green-500" />}
            label="Resolved"
            value={stats.tickets.resolved.toString()}
            sub={`${stats.tickets.closed} closed`}
            color="green"
          />
          <StatCard
            icon={<Building2 size={18} className="text-purple-600 dark:text-purple-400" />}
            label="Tenants"
            value={stats.tenants_active.toString()}
            sub={`of ${stats.tenants_total} active`}
            color="purple"
          />
        </div>
      )}

      {/* ── Ticket Summary ──────────────────────────────────────── */}
      {stats && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ticket Summary</h3>
            <Link to="/tickets" className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <TicketStatusBadge label="Open"     count={stats.tickets.open}     color="red"    />
            <TicketStatusBadge label="Pending"  count={stats.tickets.pending}  color="amber"  />
            <TicketStatusBadge label="Resolved" count={stats.tickets.resolved} color="green"  />
            <TicketStatusBadge label="Closed"   count={stats.tickets.closed}   color="gray"   />
          </div>
        </div>
      )}

      {/* ── System Status ───────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">System Status</h3>
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Cannot reach API</div>
              <div className="text-sm mt-1">{error}</div>
              <div className="text-xs mt-2">
                Make sure the PHP backend is running at{' '}
                <code className="px-1 py-0.5 bg-red-100 dark:bg-red-800/50 rounded">
                  http://localhost/promed-support-api
                </code>
              </div>
            </div>
          </div>
        )}
        {health && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatusCard label="Application"    value={health.app} />
            <StatusCard label="Database"       value={health.database} />
            <StatusCard label="Knowledge Base" value={health.kb} />
          </div>
        )}
      </div>

      {/* ── Quick Links ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/workflows"
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-brand-500 dark:hover:border-brand-400 transition-colors"
        >
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare size={18} className="text-brand-600 dark:text-brand-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Manage Workflows</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Create, edit, and organize support workflows
          </p>
        </Link>
        <Link
          to="/test"
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-brand-500 dark:hover:border-brand-400 transition-colors"
        >
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 size={18} className="text-brand-600 dark:text-brand-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Test the Agent</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Try questions against your draft KB before publishing
          </p>
        </Link>
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  icon, label, value, sub, color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  color: 'blue' | 'amber' | 'green' | 'purple';
}) {
  const bg: Record<string, string> = {
    blue:   'bg-brand-50 dark:bg-brand-900/20',
    amber:  'bg-amber-50 dark:bg-amber-900/20',
    green:  'bg-green-50 dark:bg-green-900/20',
    purple: 'bg-purple-50 dark:bg-purple-900/20',
  };
  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-700 p-4 ${bg[color]}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span></div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{sub}</div>
    </div>
  );
}

// ─── Ticket status badge ──────────────────────────────────────────────────────

function TicketStatusBadge({ label, count, color }: { label: string; count: number; color: 'red' | 'amber' | 'green' | 'gray' }) {
  const styles: Record<string, string> = {
    red:   'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-500 border-green-200 dark:border-green-800',
    gray:  'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 flex flex-col items-center ${styles[color]}`}>
      <span className="text-2xl font-bold">{count}</span>
      <span className="text-xs font-medium mt-0.5">{label}</span>
    </div>
  );
}

// ─── Status card ──────────────────────────────────────────────────────────────

function StatusCard({ label, value }: { label: string; value: string }) {
  const isOk = !value.toLowerCase().startsWith('error');
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 dark:bg-gray-700/30">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{label}</div>
      <div className="flex items-center gap-2">
        {isOk ? (
          <CheckCircle2 size={16} className="text-green-600 dark:text-green-500" />
        ) : (
          <AlertCircle size={16} className="text-red-600 dark:text-red-500" />
        )}
        <span className="text-sm text-gray-900 dark:text-white font-medium truncate">{value}</span>
      </div>
    </div>
  );
}
